import { FC, useEffect } from 'react'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams
} from 'react-router-dom'
import { OlaggModule, PermissionEnum } from '@olagg/db-types'
import { ThirdwebProvider as ThirdwebProviderV5 } from 'thirdweb/react'

import Alerts from './pages/Alerts'
import CreateAlert from './pages/Alerts/Create'
import ArticleCtas from './pages/ArticleCtas'
import ArticlePolls from './pages/ArticlePolls'
import ReviewArticlePoll from './pages/ArticlePolls/Edit'
import CreateArticleCta from './pages/ArticleCtas/Create'
import UpdateArticleCta from './pages/ArticleCtas/Edit'
import CreateBlockchainToken from './pages/BlockchainTokenCatalog/components/blockchainCatalog/Create'
import CreateProduct from './pages/Marketplace/products/Create'
import CreateMiniGame from './pages/MiniGames/Create';
import CreateNotification from './pages/Notification/CreateNotification'
import CreateRelevantAction from './pages/RelevantActions/Create'
import CreateRole from './pages/Permissions/Roles/Create'
import CreateRolesPermission from './pages/Permissions/RolesPermissions/Create'
import CreateSegment from './pages/Notification/CreateSegment'
import EditSegment from './pages/Notification/EditSegment'
import CreateTokenDrops from './pages/TokenDrops/Create'
import CreateTournament from './pages/Tournaments/Create'
import Credentials from './pages/Credentials'
import EditBlockchainToken from './pages/BlockchainTokenCatalog/components/blockchainCatalog/Edit'
import EditMiniGame from './pages/MiniGames/Edit';
import TreasureGameOpportunities from './pages/MiniGames/ExtraOpportunities';
import CreateTreasureOpportunity from './pages/MiniGames/ExtraOpportunities/Create';
import EditTreasureOpportunity from './pages/MiniGames/ExtraOpportunities/Edit';
import EditTournament from './pages/Tournaments/Edit'
import Error from './pages/Error'
import Games from './pages/Games'
import GamesContent from './pages/GameContent'
import GamesContentCreate from './pages/GameContent/create'
import GamesContentEdit from './pages/GameContent/edit'
import Home from './pages/Home'
import Layout from './components/Layout'
import { Login } from '@olagg/ui-kit'
import Maintenance from '@olagg/ui-kit/Maintenance'
import ManageCredentials from './pages/ManageCredentials'
import ManageGame from './pages/ManageGame'
import ManageQuest from './pages/ManageQuest'
import ManageQuestOwner from './pages/ManageQuestOwner'
import ManageUser from './pages/ManageUser'
import ManageUserContent from './pages/ManageUserContent'
import Marketplace from './pages/Marketplace'
import MarketplaceCreate from './pages/Marketplace/create'
import MarketplaceEdit from './pages/Marketplace/edit'
import MiniGames from './pages/MiniGames';
import Notifications from './pages/Notification/Notifications'
import { PushNotifications } from './pages/PushNotification/PushNotifications'
import { CreatePushNotification } from './pages/PushNotification/CreatePushNotification'
import { EditPushNotification } from './pages/PushNotification/EditPushNotification'
import Payments from './pages/Payments/Payments'
import QuestBatchComplete from './pages/QuestBatchComplete'
import QuestOwners from './pages/QuestOwners'
import Quests from './pages/Quests'
import { ROUTES } from './consts/routes'
import RelevantActions from './pages/RelevantActions'
import RequireAuth from './components/RequireAuth'
import Roles from './pages/Permissions/Roles'
import RolesPermissions from './pages/Permissions/RolesPermissions'
import Roulette from './pages/Roulette'
import Scholarships from './pages/Scholarships'
import Segments from './pages/Notification/Segments'
import TokenAddWhitelistedUser from './pages/Tokens/TokenAddWhitelistedUser'
import TokenAddWhitelistedUsers from './pages/Tokens/Assignments/CreateBatch'
import TokenAssignments from './pages/Tokens/Assignments'
import TokenDrops from './pages/TokenDrops/TokenDrops'
import TokenForm from './pages/Tokens/TokenForm'
import Tokens from './pages/Tokens'
import TournamentAddInscription from './pages/Tournaments/Inscriptions/Create'
import TournamentInscriptions from './pages/Tournaments/Inscriptions/Inscriptions'
import Tournaments from './pages/Tournaments'
import UpdateAlert from './pages/Alerts/Edit'
import UpdateRelevantAction from './pages/RelevantActions/Edit'
import UpdateRole from './pages/Permissions/Roles/Edit'
import UpdateTokenDrop from './pages/TokenDrops/Update'
import UserContent from './pages/UserContent'
import UserPermissions from './pages/Permissions'
import Users from './pages/Users'
import { useAuthStore } from '@olagg/store'
import useRoles from './features/Roles/hooks/useRoles';
import { useRolesStore } from '@olagg/store'
import Surveys from './pages/Surveys/Surveys'
import SurveysEdit from './pages/Surveys/SurveysEdit'
import UserPreferences from './pages/UserPreferences/UserPreferences'
import UserPreferencesEdit from './pages/UserPreferences/UserPreferencesEdit'
import Stories from './pages/Stories/Stories'
import UpdateStory from './pages/Stories/components/stories/Edit'
import CreateStory from './pages/Stories/components/stories/Create'
import UpdateStoryCategory from './pages/Stories/components/storyCategories/Edit'
import CreateStoryCategory from './pages/Stories/components/storyCategories/Create'
import BlockchainCatalogTabs from './pages/BlockchainTokenCatalog';
import BlockchainNetworksList from './pages/BlockchainTokenCatalog/components/blockchainNetworks';
import EditBlockchainNetwork from './pages/BlockchainTokenCatalog/components/blockchainNetworks/Edit';
import CreateBlockchainNetwork from './pages/BlockchainTokenCatalog/components/blockchainNetworks/Create';
import Streaks from './pages/Streaks/Streaks';
import CardDecks, { EditCardDeck, CreateCardDeck } from './pages/CardDecks';
import DiscordServers, { EditServer, CreateServer } from './pages/CardDecks/Servers';
import CardDeckTraits, { EditTrait, CreateTrait } from './pages/CardDecks/Traits';
import CardDeckVariants, { EditVariant, CreateVariant } from './pages/CardDecks/Variants';
import ProductsList from './pages/Marketplace/products';
import EditProduct from './pages/Marketplace/products/Edit';
import ProductInventory from './pages/Marketplace/products/Inventory';
import Events from './pages/Events/Events';
import UpdateEvent from './pages/Events/UpdateEvent';
import UpdateHost from './pages/Events/UpdateHost';
import PercentageCoupon from './pages/Marketplace/products/components/PercentageCoupon';
import PercentagePromoCoupon from './pages/Marketplace/products/components/PercentagePromoCoupon';
import Seasons from './pages/Seasons';
import CreateSeason from './pages/Seasons/Create';
import EditSeason from './pages/Seasons/Edit';
import Challenges from './pages/Seasons/challenges';
import CreateChallenge from './pages/Seasons/challenges/Create';
import EditChallenge from './pages/Seasons/challenges/Edit';
import Tasks from './pages/Seasons/challenges/tasks';
import CreateTask from './pages/Seasons/challenges/tasks/Create';
import EditTask from './pages/Seasons/challenges/tasks/Edit';
import ClawMachinePrizes from './pages/ClawMachine';
import CreateClawMachinePrize from './pages/ClawMachine/Create';
import EditClawMachinePrize from './pages/ClawMachine/Edit';
import GameSeasonClawMachinePrizes from './pages/Seasons/ClawPrizes';
import CreateSeasonClawPrize from './pages/Seasons/ClawPrizes/Create';
import EditSeasonClawPrize from './pages/Seasons/ClawPrizes/Edit';
import GameSeasonClawMachinePrizeClaims from './pages/Seasons/ClawPrizes/ClawPrizeClaims';
import LevelRewards from './pages/LevelRewards'
import CreateLevelReward from './pages/LevelRewards/Create'
import EditLevelReward from './pages/LevelRewards/Edit'
import ReactorEconomy from './pages/ReactorEconomy'
import PurchaseRewardPromotions from './pages/PurchaseRewardPromotions';
import CreatePurchaseRewardPromotion from './pages/PurchaseRewardPromotions/Create';
import EditPurchaseRewardPromotion from './pages/PurchaseRewardPromotions/Edit';
import Trivia from './pages/Trivia';
import CreateTriviaQuestion from './pages/Trivia/Create';
import EditTriviaQuestion from './pages/Trivia/Edit';
import AgentNews from './pages/AgentNews/AgentNews';
import EditAgentNews from './pages/AgentNews/components/Edit';

const App: FC = () => {
  const { me, loggedIn, getMe } = useAuthStore()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { roles, initRoles, initPermissions } = useRolesStore();
  const { rolesForModule } = useRoles();
  const referrerId =
    searchParams.get('referrerId') ||
    localStorage.getItem('referrerId') ||
    ''

  if (searchParams.get('referrerId'))
    localStorage.setItem('referrerId', referrerId)

  const utm_path =
    searchParams.get('utm_path') ||
    localStorage.getItem('utm_path') ||
    ''

  if (searchParams.get('utm_path'))
    localStorage.setItem('utm_path', utm_path)

  useEffect(() => {
    document.body.style.backgroundColor = `#0C0324`
    getMe()
  }, [, pathname])

  useEffect(() => {
    const c = setTimeout(() => {
      if (me) {
        localStorage.removeItem('utm_path')

        if (utm_path) {
          navigate(utm_path)
        }
      }
    }, 5)

    return () => clearTimeout(c)
  }, [me])

  useEffect(() => {
    initRoles();
    initPermissions();
  }, []);

  const {
    LOGIN,
    HOME,
    SCHOLARSHIP_APPLICATIONS,
    GAMES,
    CREDENTIALS,
    USERS,
    OWNERS,
    QUESTS,
    USER_CONTENT,
    NOTIFICATIONS,
    PUSH_NOTIFICATIONS,
    SEGMENTS,
    MARKETPLACE,
    TOURNAMENTS,
    TOKENS,
    RELEVANT_ACTIONS,
    ALERTS,
    ARTICLE_CTAS,
    ARTICLE_POLLS,
    USER_PERMISSIONS,
    ROLES,
    ROLES_PERMISSIONS,
    ROULETTE,
    TOKEN_DROPS,
    PAYMENTS,
    SURVEYS,
    USER_PREFERENCES,
    STORIES,
    STORY_CATEGORIES,
    BLOCKCHAIN_TOKEN_CATALOG,
    BLOCKCHAIN_TOKEN_NETWORKS,
    MINI_GAMES,
    STREAKS,
    CARD_DECKS,
    PRODUCTS,
    EVENTS,
    HOSTS,
    SEASONS,
    CLAW_MACHINE_PRIZES,
    LEVEL_REWARDS,
    PURCHASE_REWARD_PROMOTIONS,
    TRIVIA,
    AGENT_NEWS,
    REACTOR_ECONOMY
  } = ROUTES

  return import.meta.env.VITE_MAINTENANCE === 'TRUE' ? (
    <Maintenance />
  ) : (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route
          index
          element={
            loggedIn ? <Navigate to={HOME} /> : <Login
              onClickLogin={provider => {
                localStorage.setItem(
                  'provider',
                  provider
                )
              }}
              referrerId={referrerId}
              redirect={utm_path}
            />
          }
        />
        <Route
          path={LOGIN}
          element={!loggedIn ? <Login
            onClickLogin={provider => {
              localStorage.setItem(
                'provider',
                provider
              )
            }}
            referrerId={referrerId}
            redirect={utm_path}
          /> : <Navigate to={'/'} />}
        />
        <Route
          path={HOME}
          element={
            <RequireAuth
              roles={roles
                .map(role => role.name.toUpperCase())
                .filter(role => role !== 'USER')
              }
              me={me}
              loggedIn={loggedIn}
            >
              <Home />
            </RequireAuth>
          }
        />
        <Route
          path={GAMES.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Game)}
              me={me}
              loggedIn={loggedIn}
            >
              <Games />
            </RequireAuth>
          }
        />
        <Route
          path={GAMES.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Game, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <ManageGame mode="create" />
            </RequireAuth>
          }
        />
        <Route
          path={SCHOLARSHIP_APPLICATIONS}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Scholarship)}
              me={me}
              loggedIn={loggedIn}
            >
              <Scholarships />
            </RequireAuth>
          }
        />
        <Route
          path={CREDENTIALS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Credential)}
              me={me}
              loggedIn={loggedIn}
            >
              <Credentials />
            </RequireAuth>
          }
        />
        <Route
          path={USERS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.User)}
              me={me}
              loggedIn={loggedIn}
            >
              <Users />
            </RequireAuth>
          }
        />
        <Route
          path={GAMES.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Game, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <ManageGame mode="edit" />
            </RequireAuth>
          }
        />
        <Route
          path={GAMES.content}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Game, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <GamesContent />
            </RequireAuth>
          }
        />
        <Route
          path={GAMES.content_create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Game, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <GamesContentCreate />
            </RequireAuth>
          }
        />
        <Route
          path={GAMES.content_update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Game, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <GamesContentEdit />
            </RequireAuth>
          }
        />
        <Route
          path={MARKETPLACE.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Item)}
              me={me}
              loggedIn={loggedIn}
            >
              <Marketplace />
            </RequireAuth>
          }
        />
        <Route
          path={MARKETPLACE.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Item, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <MarketplaceCreate />
            </RequireAuth>
          }
        />
        <Route
          path={MARKETPLACE.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Item, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <MarketplaceEdit />
            </RequireAuth>
          }
        />
        <Route
          path={PRODUCTS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Product, PermissionEnum.ALL)}
              me={me}
              loggedIn={loggedIn}
            >
              <ProductsList />
            </RequireAuth>
          }
        />
        <Route
          path={PRODUCTS.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Product, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateProduct />
            </RequireAuth>
          }
        />
        <Route
          path={PRODUCTS.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Product, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <EditProduct />
            </RequireAuth>
          }
        />
        <Route
          path={PRODUCTS.inventory}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Product, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <ProductInventory />
            </RequireAuth>
          }
        />
        <Route
          path={PRODUCTS.showCoupon}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Product, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <PercentageCoupon />
            </RequireAuth>
          }
        />
        <Route
          path={PRODUCTS.createCoupon}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Product, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <PercentageCoupon />
            </RequireAuth>
          }
        />
        <Route
          path={PRODUCTS.showPromoCoupon}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Product, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <PercentagePromoCoupon />
            </RequireAuth>
          }
        />
        <Route
          path={PRODUCTS.createPromoCoupon}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Product, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <PercentagePromoCoupon />
            </RequireAuth>
          }
        />
        <Route
          path={TOKENS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Token)}
              me={me}
              loggedIn={loggedIn}
            >
              <Tokens />
            </RequireAuth>
          }
        />
        <Route
          path={TOKENS.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Token, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <ThirdwebProviderV5>
                <TokenForm mode='create' />
              </ThirdwebProviderV5>
            </RequireAuth>
          }
        />
        <Route
          path={TOKENS.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Token, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <ThirdwebProviderV5>
                <TokenForm mode='edit' />
              </ThirdwebProviderV5>
            </RequireAuth>
          }
        />
        <Route
          path={TOKENS.whitelist}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.TokenAssignment)}
              me={me}
              loggedIn={loggedIn}
            >
              <ThirdwebProviderV5>
                <TokenAssignments />
              </ThirdwebProviderV5>
            </RequireAuth>
          }
        />
        <Route
          path={TOKENS.addWhitelistedUser}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.TokenAssignment, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <ThirdwebProviderV5>
                <TokenAddWhitelistedUser />
              </ThirdwebProviderV5>
            </RequireAuth>
          }
        />
        <Route
          path={TOKENS.addWhitelistedUsers}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.TokenAssignment, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <ThirdwebProviderV5>
                <TokenAddWhitelistedUsers />
              </ThirdwebProviderV5>
            </RequireAuth>
          }
        />
        <Route
          path={CREDENTIALS.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Credential, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <ManageCredentials mode="create" />
            </RequireAuth>
          }
        />
        <Route
          path={CREDENTIALS.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Credential, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <ManageCredentials mode="edit" />
            </RequireAuth>
          }
        />
        <Route
          path={USERS.show}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.User, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <ManageUser />
            </RequireAuth>
          }
        />
        <Route
          path={OWNERS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Owner)}
              me={me}
              loggedIn={loggedIn}
            >
              <QuestOwners />
            </RequireAuth>
          }
        />
        <Route
          path={OWNERS.show}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Owner, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <ManageQuestOwner mode="edit" />
            </RequireAuth>
          }
        />
        <Route
          path={OWNERS.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Owner, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <ManageQuestOwner mode="create" />
            </RequireAuth>
          }
        />
        <Route
          path={QUESTS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Quest)}
              me={me}
              loggedIn={loggedIn}
            >
              <Quests />
            </RequireAuth>
          }
        />
        <Route
          path={QUESTS.show}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Quest, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <ManageQuest mode="edit" />
            </RequireAuth>
          }
        />
        <Route
          path={QUESTS.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Quest, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <ManageQuest mode="create" />
            </RequireAuth>
          }
        />
        <Route
          path={QUESTS.batch_complete}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Quest, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <QuestBatchComplete />
            </RequireAuth>
          }
        />
        <Route
          path={USER_CONTENT.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.UserContent)}
              me={me}
              loggedIn={loggedIn}
            >
              <UserContent />
            </RequireAuth>
          }
        />
        <Route
          path={USER_CONTENT.show}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.UserContent, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <ManageUserContent mode='edit' />
            </RequireAuth>
          }
        />
        <Route
          path={NOTIFICATIONS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Notification)}
              me={me}
              loggedIn={loggedIn}
            >
              <Notifications />
            </RequireAuth>
          }
        />
        <Route
          path={NOTIFICATIONS.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Notification, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateNotification />
            </RequireAuth>
          }
        />
        <Route
          path={PUSH_NOTIFICATIONS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Notification)}
              me={me}
              loggedIn={loggedIn}
            >
              <PushNotifications />
            </RequireAuth>
          }
        />
        <Route
          path={PUSH_NOTIFICATIONS.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Notification, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreatePushNotification />
            </RequireAuth>
          }
        />
        <Route
          path={PUSH_NOTIFICATIONS.edit}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Notification, PermissionEnum.UPDATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <EditPushNotification />
            </RequireAuth>
          }
        />
        <Route
          path={SEGMENTS.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Notification, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateSegment />
            </RequireAuth>
          }
        />
        <Route
          path="/segments/:id/edit"
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Notification, PermissionEnum.UPDATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <EditSegment />
            </RequireAuth>
          }
        />
        <Route
          path={SEGMENTS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Notification)}
              me={me}
              loggedIn={loggedIn}
            >
              <Segments />
            </RequireAuth>
          }
        />
        <Route path="*" element={<Error />} />
        <Route
          path={TOURNAMENTS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Tournament)}
              me={me}
              loggedIn={loggedIn}
            >
              <Tournaments />
            </RequireAuth>
          }
        />
        <Route
          path={TOURNAMENTS.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Tournament, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateTournament />
            </RequireAuth>
          }
        />
        <Route
          path={TOURNAMENTS.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Tournament, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <EditTournament />
            </RequireAuth>
          }
        />
        <Route
          path={TOURNAMENTS.inscriptions}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Inscription)}
              me={me}
              loggedIn={loggedIn}
            >
              <TournamentInscriptions />
            </RequireAuth>
          }
        />
        <Route
          path={TOURNAMENTS.addInscription}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Inscription, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <TournamentAddInscription />
            </RequireAuth>
          }
        />
        <Route
          path={RELEVANT_ACTIONS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.RelevantAction)}
              me={me}
              loggedIn={loggedIn}
            >
              <RelevantActions />
            </RequireAuth>
          }
        />
        <Route
          path={RELEVANT_ACTIONS.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.RelevantAction, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateRelevantAction />
            </RequireAuth>
          }
        />
        <Route
          path={RELEVANT_ACTIONS.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.RelevantAction, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <UpdateRelevantAction />
            </RequireAuth>
          }
        />
        <Route
          path={ALERTS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Alert)}
              me={me}
              loggedIn={loggedIn}
            >
              <Alerts />
            </RequireAuth>
          }
        />
        <Route
          path={ALERTS.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Alert, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateAlert />
            </RequireAuth>
          }
        />
        <Route
          path={ALERTS.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Alert, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <UpdateAlert />
            </RequireAuth>
          }
        />
        <Route
          path={ARTICLE_CTAS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.ArticleCta)}
              me={me}
              loggedIn={loggedIn}
            >
              <ArticleCtas />
            </RequireAuth>
          }
        />
        <Route
          path={ARTICLE_CTAS.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.ArticleCta, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateArticleCta />
            </RequireAuth>
          }
        />
        <Route
          path={ARTICLE_CTAS.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.ArticleCta, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <UpdateArticleCta />
            </RequireAuth>
          }
        />
        <Route
          path={ARTICLE_POLLS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.ArticlePoll)}
              me={me}
              loggedIn={loggedIn}
            >
              <ArticlePolls />
            </RequireAuth>
          }
        />
        <Route
          path={ARTICLE_POLLS.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.ArticlePoll)}
              me={me}
              loggedIn={loggedIn}
            >
              <ReviewArticlePoll />
            </RequireAuth>
          }
        />
        <Route
          path={USER_PERMISSIONS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Role)}
              me={me}
              loggedIn={loggedIn}
            >
              <UserPermissions />
            </RequireAuth>
          }
        />
        <Route
          path={ROLES.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Role)}
              me={me}
              loggedIn={loggedIn}
            >
              <Roles />
            </RequireAuth>
          }
        />
        <Route
          path={ROLES.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Role, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateRole />
            </RequireAuth>
          }
        />
        <Route
          path={ROLES.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Role, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <UpdateRole />
            </RequireAuth>
          }
        />
        <Route
          path={ROLES_PERMISSIONS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Role)}
              me={me}
              loggedIn={loggedIn}
            >
              <RolesPermissions />
            </RequireAuth>
          }
        />
        <Route
          path={ROLES_PERMISSIONS.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Role, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateRolesPermission />
            </RequireAuth>
          }
        />
        <Route
          path={ROULETTE.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.RouletteValue, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <Roulette />
            </RequireAuth>
          }
        />
        <Route
          path={TOKEN_DROPS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.TokenDrop, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <TokenDrops />
            </RequireAuth>
          }
        />
        <Route
          path={TOKEN_DROPS.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.TokenDrop, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateTokenDrops />
            </RequireAuth>
          }
        />
        <Route
          path={TOKEN_DROPS.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.TokenDrop, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <UpdateTokenDrop />
            </RequireAuth>
          }
        />
        <Route
          path={PAYMENTS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Payment, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <Payments />
            </RequireAuth>
          }
        />
        <Route
          path={MINI_GAMES.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.TreasuresGame)}
              me={me}
              loggedIn={loggedIn}
            >
              <MiniGames />
            </RequireAuth>
          }
        />
        <Route
          path={STORIES.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Story, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <Stories />
            </RequireAuth>
          }
        />
        <Route
          path={MINI_GAMES.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.TreasuresGame)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateMiniGame />
            </RequireAuth>
          }
        />
        <Route
          path={STORIES.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Story, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <UpdateStory />
            </RequireAuth>
          }
        />
        <Route
          path={MINI_GAMES.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.TreasuresGame, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <EditMiniGame />
            </RequireAuth>
          }
        />
        <Route
          path={MINI_GAMES.treasure_opportunities.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.TreasuresGame, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <TreasureGameOpportunities />
            </RequireAuth>
          }
        />
        <Route
          path={MINI_GAMES.treasure_opportunities.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.TreasuresGame, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateTreasureOpportunity />
            </RequireAuth>
          }
        />
        <Route
          path={MINI_GAMES.treasure_opportunities.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.TreasuresGame, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <EditTreasureOpportunity />
            </RequireAuth>
          }
        />
        <Route
          path={STORIES.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Story, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateStory />
            </RequireAuth>
          }
        />
        <Route
          path={STORY_CATEGORIES.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Story, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <UpdateStoryCategory />
            </RequireAuth>
          }
        />
        <Route
          path={STORY_CATEGORIES.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Story, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateStoryCategory />
            </RequireAuth>
          }
        />
        <Route
          path={BLOCKCHAIN_TOKEN_CATALOG.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.BlockchainToken, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <BlockchainCatalogTabs />
            </RequireAuth>
          }
        />
        <Route
          path={BLOCKCHAIN_TOKEN_CATALOG.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.BlockchainToken, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateBlockchainToken />
            </RequireAuth>
          }
        />
        <Route
          path={BLOCKCHAIN_TOKEN_CATALOG.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.BlockchainToken, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <EditBlockchainToken />
            </RequireAuth>
          }
        />
        <Route
          path={BLOCKCHAIN_TOKEN_NETWORKS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.BlockchainTokenNetwork, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <BlockchainNetworksList />
            </RequireAuth>
          }
        />
        <Route
          path={BLOCKCHAIN_TOKEN_NETWORKS.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.BlockchainTokenNetwork, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateBlockchainNetwork />
            </RequireAuth>
          }
        />
        <Route
          path={BLOCKCHAIN_TOKEN_NETWORKS.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.BlockchainTokenNetwork, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <EditBlockchainNetwork />
            </RequireAuth>
          }
        />
        <Route
          path={SURVEYS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Survey, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <Surveys />
            </RequireAuth>
          }
        />
        <Route
          path={SURVEYS.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Survey, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <SurveysEdit />
            </RequireAuth>
          }
        />
        <Route
          path={USER_PREFERENCES.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.GameCategory, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <UserPreferences />
            </RequireAuth>
          }
        />
        <Route
          path={USER_PREFERENCES.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.GameCategory, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <UserPreferencesEdit />
            </RequireAuth>
          }
        />
        <Route
          path={STREAKS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Streak, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <Streaks />
            </RequireAuth>
          }
        />
        <Route
          path={CARD_DECKS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.CardDeck, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <CardDecks />
            </RequireAuth>
          }
        />
        <Route
          path={CARD_DECKS.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.CardDeck, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <EditCardDeck />
            </RequireAuth>
          }
        />
        <Route
          path={CARD_DECKS.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.CardDeck, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateCardDeck />
            </RequireAuth>
          }
        />
        <Route
          path={CARD_DECKS.servers.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.CardDeck, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <DiscordServers />
            </RequireAuth>
          }
        />
        <Route
          path={CARD_DECKS.servers.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.CardDeck, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <EditServer />
            </RequireAuth>
          }
        />
        <Route
          path={CARD_DECKS.servers.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.CardDeck, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateServer />
            </RequireAuth>
          }
        />
        <Route
          path={CARD_DECKS.traits.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.CardDeck, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <CardDeckTraits />
            </RequireAuth>
          }
        />
        <Route
          path={CARD_DECKS.traits.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.CardDeck, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <EditTrait />
            </RequireAuth>
          }
        />
        <Route
          path={CARD_DECKS.traits.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.CardDeck, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateTrait />
            </RequireAuth>
          }
        />
        <Route
          path={CARD_DECKS.variants.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.CardDeck, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <CardDeckVariants />
            </RequireAuth>
          }
        />
        <Route
          path={CARD_DECKS.variants.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.CardDeck, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <EditVariant />
            </RequireAuth>
          }
        />
        <Route
          path={CARD_DECKS.variants.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.CardDeck, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateVariant />
            </RequireAuth>
          }
        />
        <Route
          path={EVENTS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Event, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <Events />
            </RequireAuth>
          }
        />
        <Route
          path={EVENTS.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Event, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <UpdateEvent />
            </RequireAuth>
          }
        />
        <Route
          path={HOSTS.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Event, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <UpdateHost />
            </RequireAuth>
          }
        />
        <Route
          path={SEASONS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Season, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <Seasons />
            </RequireAuth>
          }
        />
        <Route
          path={SEASONS.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Season, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateSeason />
            </RequireAuth>
          }
        />
        <Route
          path={SEASONS.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Season, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <EditSeason />
            </RequireAuth>
          }
        />
        <Route
          path={SEASONS.challenges.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Season, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <Challenges />
            </RequireAuth>
          }
        />
        <Route
          path={SEASONS.challenges.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Season, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateChallenge />
            </RequireAuth>
          }
        />
        <Route
          path={SEASONS.challenges.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Season, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <EditChallenge />
            </RequireAuth>
          }
        />
        <Route
          path={SEASONS.tasks.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Season, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <Tasks />
            </RequireAuth>
          }
        />
        <Route
          path={SEASONS.tasks.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Season, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateTask />
            </RequireAuth>
          }
        />
        <Route
          path={SEASONS.tasks.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Season, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <EditTask />
            </RequireAuth>
          }
        />
        <Route
          path={SEASONS.claw_machine_prizes.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Season, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <GameSeasonClawMachinePrizes />
            </RequireAuth>
          }
        />
        <Route
          path={SEASONS.claw_machine_prizes.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Season, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateSeasonClawPrize />
            </RequireAuth>
          }
        />
        <Route
          path={SEASONS.claw_machine_prizes.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Season, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <EditSeasonClawPrize />
            </RequireAuth>
          }
        />
        <Route
          path={SEASONS.claw_machine_prizes.claims}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Season, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <GameSeasonClawMachinePrizeClaims />
            </RequireAuth>
          }
        />
        <Route
          path={SEASONS.claw_machine_prizes.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Season, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <GameSeasonClawMachinePrizes />
            </RequireAuth>
          }
        />
        <Route
          path={CLAW_MACHINE_PRIZES.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.ClawMachinePrize, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <ClawMachinePrizes />
            </RequireAuth>
          }
        />
        <Route
          path={CLAW_MACHINE_PRIZES.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.ClawMachinePrize, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateClawMachinePrize />
            </RequireAuth>
          }
        />
        <Route
          path={CLAW_MACHINE_PRIZES.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.ClawMachinePrize, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <EditClawMachinePrize />
            </RequireAuth>
          }
        />
        <Route
          path={LEVEL_REWARDS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.LevelReward, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <LevelRewards />
            </RequireAuth>
          }
        />
        <Route
          path={REACTOR_ECONOMY.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.ReactorEconomyConfig, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <ReactorEconomy />
            </RequireAuth>
          }
        />
        <Route
          path={PURCHASE_REWARD_PROMOTIONS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.PurchaseRewardPromotion, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <PurchaseRewardPromotions />
            </RequireAuth>
          }
        />
        <Route
          path={LEVEL_REWARDS.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.LevelReward, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateLevelReward />
            </RequireAuth>
          }
        />
        <Route
          path={PURCHASE_REWARD_PROMOTIONS.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.PurchaseRewardPromotion, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreatePurchaseRewardPromotion />
            </RequireAuth>
          }
        />
        <Route
          path={LEVEL_REWARDS.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.LevelReward, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <EditLevelReward />
            </RequireAuth>
          }
        />
        <Route
          path={PURCHASE_REWARD_PROMOTIONS.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.PurchaseRewardPromotion, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <EditPurchaseRewardPromotion />
            </RequireAuth>
          }
        />
        <Route
          path={TRIVIA.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Trivia, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <Trivia />
            </RequireAuth>
          }
        />
        <Route
          path={TRIVIA.create}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Trivia, PermissionEnum.CREATE)}
              me={me}
              loggedIn={loggedIn}
            >
              <CreateTriviaQuestion />
            </RequireAuth>
          }
        />
        <Route
          path={TRIVIA.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.Trivia, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <EditTriviaQuestion />
            </RequireAuth>
          }
        />
        <Route
          path={AGENT_NEWS.all}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.News, PermissionEnum.VIEW)}
              me={me}
              loggedIn={loggedIn}
            >
              <AgentNews />
            </RequireAuth>
          }
        />
        <Route
          path={AGENT_NEWS.update}
          element={
            <RequireAuth
              roles={rolesForModule(OlaggModule.News, PermissionEnum.EDIT)}
              me={me}
              loggedIn={loggedIn}
            >
              <EditAgentNews />
            </RequireAuth>
          }
        />
      </Route>
    </Routes>
  )
}

export default App
